import { ArrowRight, Clock, ShieldCheck } from "lucide-react";
import { useTranslation } from "@thyra/landing-i18n";
import { ToolIcon } from "@thyra/ui/tool-icon";
import {
  formatWeekdays,
  type CatalogAgent,
  type Cadence,
} from "../../lib/agent-catalog";
import { LandingAgentIcon } from "../landing-agent-icon";
import { ConnectionLogos } from "./ConnectionLogos";
import { HoodRow } from "./HoodRow";

const TOOLS_SHOWN = 3;

function useCadenceLabel() {
  const { t, i18n } = useTranslation("sections");
  return (cadence: Cadence): string => {
    switch (cadence.kind) {
      case "interval":
        return t(
          cadence.unit === "hours"
            ? "assistantRoles.cadence.everyHours"
            : "assistantRoles.cadence.everyMinutes",
          { count: cadence.count },
        );
      case "daily":
        return t("assistantRoles.cadence.daily", { time: cadence.time });
      case "weekdays":
        return t("assistantRoles.cadence.weekdays", { time: cadence.time });
      case "weekly":
        return t("assistantRoles.cadence.weekly", {
          days: formatWeekdays(cadence.days, i18n.language),
          time: cadence.time,
        });
      default:
        return t("assistantRoles.cadence.scheduled");
    }
  };
}

type AgentCardProps = {
  agent: CatalogAgent;
  categoryLabel?: string;
  /** Where the call to action goes; the section decides, so an invalid id is never deep-linked. */
  href: string;
  ctaLabel: string;
  delayMs: number;
  /** The accounts are unknown (offline preview), so say nothing about them. */
  hideAccounts?: boolean;
  /** Sizing/snap classes from the carousel that lays the card out. */
  className?: string;
};

export function AgentCard({
  agent,
  categoryLabel,
  href,
  ctaLabel,
  delayMs,
  hideAccounts = false,
  className = "",
}: AgentCardProps) {
  const { t } = useTranslation("sections");
  const cadenceLabel = useCadenceLabel();
  const tools = agent.tools.slice(0, TOOLS_SHOWN);
  const extraTools = agent.tools.length - tools.length;
  const showAccounts =
    !hideAccounts &&
    (agent.connections.length > 0 || agent.optionalConnections.length > 0);

  return (
    <li
      className={`agent-card-enter marketing-card marketing-card-hover group relative flex flex-col gap-2.5 p-4 ${className}`}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <ArrowRight
        className="absolute right-3.5 top-4 h-4 w-4 text-muted-foreground transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-primary"
        aria-hidden
      />
      <div className="flex items-start gap-3 pr-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
          <LandingAgentIcon iconKey={agent.iconKey} className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold leading-snug tracking-tight text-card-foreground line-clamp-2">
            <a
              href={href}
              rel="nofollow"
              className="outline-none after:absolute after:inset-0 after:rounded-[inherit] after:content-[''] focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-primary"
            >
              {agent.name}
              <span className="sr-only">: {ctaLabel}</span>
            </a>
          </h3>
          {categoryLabel ? (
            <p className="truncate text-xs font-medium leading-4 text-muted-foreground">
              {categoryLabel}
            </p>
          ) : null}
        </div>
      </div>

      <p className="text-sm leading-snug text-muted-foreground-strong line-clamp-2">
        {agent.description || t("assistantRoles.defaultDescription")}
      </p>

      {agent.requiresApproval || agent.cadence ? (
        <div className="flex flex-wrap gap-1.5">
          {agent.requiresApproval ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <ShieldCheck
                className="h-3.5 w-3.5"
                strokeWidth={2}
                aria-hidden
              />
              {t("assistantRoles.badges.asksFirst")}
            </span>
          ) : null}
          {agent.cadence ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground-strong">
              <Clock className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              {cadenceLabel(agent.cadence)}
            </span>
          ) : null}
        </div>
      ) : null}

      {showAccounts ? (
        <ConnectionLogos
          connections={agent.connections}
          optionalConnections={agent.optionalConnections}
        />
      ) : null}

      {tools.length > 0 ? (
        <ul className="flex min-w-0 items-center gap-1 overflow-hidden">
          {tools.map((tool) => (
            <li
              key={tool}
              className="flex min-w-0 items-center gap-1.5 rounded-md border border-border-subtle bg-muted/50 py-0.5 pl-0.5 pr-1.5 text-xs text-muted-foreground-strong"
            >
              <ToolIcon toolName={tool} size="xs" />
              <span className="min-w-0 truncate">{tool}</span>
            </li>
          ))}
          {extraTools > 0 ? (
            <li
              className="shrink-0 px-1 text-xs font-medium text-muted-foreground"
              title={agent.tools.join(", ")}
            >
              +{extraTools}
            </li>
          ) : null}
        </ul>
      ) : null}

      <HoodRow agent={agent} />
    </li>
  );
}

export function AgentCardSkeleton({ className = "" }: { className?: string }) {
  return (
    <li
      aria-hidden
      className={`marketing-card flex flex-col gap-2.5 p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-muted" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/3 animate-pulse rounded bg-muted/70" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 w-full animate-pulse rounded bg-muted/80" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-muted/80" />
      </div>
      <div className="h-5 w-1/2 animate-pulse rounded-full bg-muted/80" />
      <div className="h-6 w-3/4 animate-pulse rounded-full bg-muted/70" />
      <div className="mt-auto h-4 w-1/2 animate-pulse rounded bg-muted/60" />
    </li>
  );
}
